import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import TokenPersistenceManager from "./tokenPersistenceManager";
import {
  REMOVE_TOKEN,
  SET_TOKEN,
} from "../Components/Views/AuthModule/services/authReducer";

const fetchBaseQueryCb = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL,

  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.token;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const customFetchBaseQueryCb = async (arg, api, extraOptions) => {
  const retryableStatusCodes = [408, 500, 502, 503, 504];
  let attempts = 0;
  const maxAttempts = 3;

  while (attempts < maxAttempts) {
    try {
      let result = await fetchBaseQueryCb(arg, api, extraOptions);

      // Handle 401 unauthorized with token refresh
      if (result?.error?.status === 401) {
        const originalRequest = { ...arg };

        if (isRefreshing) {
          try {
            // Wait for the token refresh
            const newToken = await new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            });

            // Retry the original request with new token
            return await fetchBaseQueryCb(originalRequest, api, extraOptions);
          } catch (err) {
            TokenPersistenceManager.removeToken();
            api.dispatch(REMOVE_TOKEN());
            return result;
          }
        }

        isRefreshing = true;

        try {
          const refreshToken = TokenPersistenceManager.getRefreshToken();
          // Use fetchBaseQueryCb directly for refresh token request
          const refreshResult = await fetchBaseQueryCb(
            {
              url: "/auth/store/refresh-token",
              method: "POST",
              body: { refreshToken },
            },
            api,
            extraOptions,
          );

          if (refreshResult.data?.accessToken) {
            const { accessToken } = refreshResult.data;
            TokenPersistenceManager.setToken(accessToken);
            api.dispatch(SET_TOKEN({ token: accessToken }));

            processQueue(null, accessToken);
            isRefreshing = false;

            // Retry the original request
            return await fetchBaseQueryCb(originalRequest, api, extraOptions);
          } else {
            throw new Error("Failed to refresh token");
          }
        } catch (err) {
          processQueue(err, null);
          TokenPersistenceManager.removeToken();
          api.dispatch(REMOVE_TOKEN());
          isRefreshing = false;
          return result;
        }
      }

      // Only retry on specific status codes
      if (
        result?.error?.status &&
        !retryableStatusCodes.includes(result.error.status)
      ) {
        return result;
      }

      if (!result?.data?.success) {
        result = {
          ...result,
          error: {
            error:
              result.error?.data?.error?.message || "Unknown error occurred",
            status: "CUSTOM_ERROR",
            data: result.data,
          },
          data: undefined,
        };
      }

      return result;
    } catch (error) {
      attempts++;
      if (attempts === maxAttempts) throw error;
      await new Promise(resolve =>
        setTimeout(resolve, 1000 * Math.pow(2, attempts)),
      ); // Exponential backoff
    }
  }
};

export const baseQuery = createApi({
  reducerPath: "baseQuery",
  baseQuery: customFetchBaseQueryCb,
  tagTypes: ["vendorStore", "products", "brands"],
  keepUnusedDataFor: 300, // Keep unused data in cache for 5 minutes

  endpoints: builder => ({
    fetchAllVendorProducts: builder.query({
      query: ({ page, limit }) =>
        `/product/vendor/get?page=${page}&limit=${limit}`,
      providesTags: ["products"],
    }),
    fetchAllBrands: builder.query({
      query: () => `/product/brand/vendor/get`,
      providesTags: ["brands"],
    }),
    fetchPincodeDetails: builder.query({
      query: ({ pincode }) => `/pincode/${pincode}`,
      cacheTime: 5 * 60 * 1000, // 5 minutes
    }),
    fetchPincodeDetailsDelivery: builder.query({
      query: ({ pincode }) => `/pincode/${pincode}`,
      cacheTime: 5 * 60 * 1000,
    }),
    fetchVendorStore: builder.query({
      query: () => "/vendor/store/details",
      cacheTime: 5 * 60 * 1000, // 5 minutes
      providesTags: ["vendorStore"],
    }),
    createCustomer: builder.mutation({
      query: customerData => ({
        url: "/vendor/customer/store/create",
        method: "POST",
        body: customerData,
      }),
    }),
    uploadCustomerDocuments: builder.mutation({
      query: ({ customerId, formData }) => ({
        url: `/vendor/customer/documents/${customerId}`,
        method: "POST",
        body: formData,
      }),
    }),
    fetchCustomers: builder.query({
      query: ({ page, limit, search, filters }) => ({
        url: `/vendor/customers`,
        method: "GET",
        params: {
          page,
          limit,
          search,
          storeType: filters?.storeType,
          status: filters?.status,
          city: filters?.city,
          creditLimit: filters?.creditLimit,
          sortBy: filters?.sortBy,
        },
      }),
      providesTags: ["customers"],
      refetchOnMountOrArgChange: false,
    }),
    updateCustomerStatus: builder.mutation({
      query: ({ customerId, status }) => ({
        url: `/vendor/customer/${customerId}/block-status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (result, error, { customerId }) => [
        "customers",
        { type: "customer", id: customerId },
      ],
    }),
    fetchCustomerCities: builder.query({
      query: () => `/vendor/customer-cities`,
      providesTags: ["customers"],
    }),
    fetchCustomerById: builder.query({
      query: id => `/vendor/customer/${id}/details`,
      providesTags: (result, error, id) => [{ type: "customer", id }],
    }),
    fetchCustomerRequests: builder.query({
      query: ({ page, limit, search, filters }) => ({
        url: `/vendor/customer-requests`,
        method: "GET",
        params: {
          page,
          limit,
          search,
          storeType: filters?.storeType,
          status: filters?.status,
          sortBy: filters?.sortBy,
        },
      }),
      providesTags: ["customerRequests"],
    }),
    fetchCustomerRequestById: builder.query({
      query: id => `/customerRequest/${id}/details`,
      providesTags: ["customerRequests"],
    }),
    rejectCustomerRequest: builder.mutation({
      query: ({ customerId, rejectionDetails }) => ({
        url: `/customerRequest/reject`,
        method: "POST",
        body: {
          customerStore: customerId,
          rejectionReason: rejectionDetails.reason,
          allowReconnection: rejectionDetails.allowReconnection,
          cooldown: {
            type:
              rejectionDetails.cooldown.type === "NO_COOLDOWN"
                ? "NO_COOLDOWN"
                : "CUSTOM",
            ...(rejectionDetails.cooldown.type === "CUSTOM"
              ? { timestamp: rejectionDetails.cooldown.timestamp }
              : { hours: rejectionDetails.cooldown.hours }),
          },
        },
      }),
      invalidatesTags: ["customerRequests"],
    }),
    approveCustomerRequest: builder.mutation({
      query: ({ customerId, approvalDetails, additionalNotes }) => ({
        url: `/customerRequest/approve`,
        method: "POST",
        body: {
          customerStore: customerId,
          vcConfigSettings: {
            creditLimit: approvalDetails.creditLimit,
            orderValidation: {
              validationType: approvalDetails.orderValidation.validationType,
              minOrderQty: approvalDetails.orderValidation.minOrderQty,
              minOrderValue: approvalDetails.orderValidation.minOrderValue,
              validationCondition:
                approvalDetails.orderValidation.validationCondition,
            },
            brandVisibility: approvalDetails.brandVisibility,
          },
          additionalNotes: additionalNotes,
        },
      }),
      invalidatesTags: ["customerRequests"],
    }),
    // changing request from reject to approve
    changeToApprove: builder.mutation({
      query: ({ customerStoreId, vcConfigSettings, additionalNotes }) => ({
        url: `/customerRequest/change-to-approved/${customerStoreId}`,
        method: "POST",
        body: {
          customerStore: customerStoreId,
          vcConfigSettings: {
            creditLimit: vcConfigSettings.creditLimit,
            brandVisibility: {
              visibilityType: vcConfigSettings.brandVisibility.visibilityType,
              removedBrands: vcConfigSettings.brandVisibility.removedBrands,
            },
            orderValidation: {
              validationType: vcConfigSettings.orderValidation.validationType,
              ...(vcConfigSettings.orderValidation.validationType ===
                "USER_SPECIFIC" && {
                minOrderQty: vcConfigSettings.orderValidation.minOrderQty,
                minOrderValue: vcConfigSettings.orderValidation.minOrderValue,
                validationCondition:
                  vcConfigSettings.orderValidation.validationCondition,
              }),
            },
          },
          additionalNotes: additionalNotes,
        },
      }),
      invalidatesTags: ["customerRequests"],
    }),
    updateVcConfig: builder.mutation({
      query: ({ configId, settings, additionalNotes }) => ({
        url: `/vc-config/${configId}/edit`,
        method: "POST",
        body: {
          settings,
          additionalNotes,
        },
      }),
      invalidatesTags: (result, error, { customerId }) => [
        { type: "customer", id: customerId },
      ],
    }),
    fetchCategoryTree: builder.query({
      query: params => ({
        url: "/product/category/vendor/tree",
        params: {
          status: params.status !== "ALL" ? params.status : undefined,
          search: params.search || undefined,
          sort: params.sort || undefined,
          parent_only: params.parent_only || undefined,
          limit: params.limit,
          page: params.page,
        },
      }),
      providesTags: ["categories"],
    }),
    createCategory: builder.mutation({
      query: formData => ({
        url: "/product/category/create",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["categories"],
    }),
    editCategory: builder.mutation({
      query: ({ categoryId, formData }) => ({
        url: `/product/category/${categoryId}/edit`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["categories"],
    }),
    deleteCategory: builder.mutation({
      query: categoryId => ({
        url: `/product/category/${categoryId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["categories"],
    }),
    createBrand: builder.mutation({
      query: formData => ({
        url: "/product/brand/create",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["brands"],
    }),
    fetchBrands: builder.query({
      query: params => ({
        url: params.include_deleted
          ? "/product/brand/vendor/deleted"
          : "/product/brand/vendor/get",
        params: {
          status: params.status !== "ALL" ? params.status : undefined,
          brand_type:
            params.brand_type !== "ALL" ? params.brand_type : undefined,
          search: params.search || undefined,
          sort: params.sort || undefined,
        },
      }),
      providesTags: ["brands"],
    }),
    editBrand: builder.mutation({
      query: ({ brandId, formData }) => ({
        url: `/product/brand/${brandId}`,
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: ["brands"],
    }),
    deleteBrand: builder.mutation({
      query: brandId => ({
        url: `/product/brand/${brandId}/soft-delete`,
        method: "PUT",
      }),
      invalidatesTags: ["brands"],
    }),
    restoreBrand: builder.mutation({
      query: brandId => ({
        url: `/product/brand/${brandId}/restore`,
        method: "PUT",
      }),
      invalidatesTags: ["brands"],
    }),
    fetchUnits: builder.query({
      query: params => ({
        url: "/units/all",
        params: {
          type: params?.type === "ALL" ? undefined : params?.type,
          category: params?.category === "ALL" ? undefined : params?.category,
          sortBy: params?.sortBy,
          search: params?.search,
        },
      }),
      providesTags: ["units"],
    }),
    fetchUnitsForSelection: builder.query({
      query: () => "/units/selection",
      providesTags: ["units"],
    }),
    createBaseUnit: builder.mutation({
      query: data => ({
        url: "units/base",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["units"],
    }),
    createCompositeUnit: builder.mutation({
      query: data => ({
        url: "/units/composite",
        method: "POST",
        body: {
          name: data.name,
          abbreviation: data.abbreviation,
          smallestUnitId: data.smallestUnitId,
          largerUnitId: data.largerUnitId,
          conversionFactor: data.conversionFactor,
          description: data.description,
        },
      }),
      invalidatesTags: ["units"],
    }),
    createTertiaryUnit: builder.mutation({
      query: data => ({
        url: "/units/tertiary",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["units"],
    }),
    updateUnit: builder.mutation({
      query: ({ id, data }) => ({
        url: `/units/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["units"],
    }),
    deleteUnit: builder.mutation({
      query: id => ({
        url: `/units/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["units"],
    }),
    createProductWithInventory: builder.mutation({
      query: productData => ({
        url: "/product/create-with-inventory",
        method: "POST",
        body: productData,
      }),
      invalidatesTags: ["products"],
    }),
  }),
});

export const {
  useFetchAllVendorProductsQuery,
  useFetchAllBrandsQuery,
  useLazyFetchPincodeDetailsQuery,
  useLazyFetchPincodeDetailsDeliveryQuery,
  useLazyFetchVendorStoreQuery,
  useCreateCustomerMutation,
  useUploadCustomerDocumentsMutation,
  useFetchCustomersQuery,
  useUpdateCustomerStatusMutation,
  useFetchCustomerCitiesQuery,
  useFetchCustomerByIdQuery,
  useFetchCustomerRequestsQuery,
  useFetchCustomerRequestByIdQuery,
  useRejectCustomerRequestMutation,
  useApproveCustomerRequestMutation,
  useChangeToApproveMutation,
  useUpdateVcConfigMutation,
  useFetchCategoryTreeQuery,
  useCreateCategoryMutation,
  useEditCategoryMutation,
  useDeleteCategoryMutation,
  useCreateBrandMutation,
  useFetchBrandsQuery,
  useEditBrandMutation,
  useDeleteBrandMutation,
  useRestoreBrandMutation,
  useFetchUnitsQuery,
  useFetchUnitsForSelectionQuery,
  useCreateBaseUnitMutation,
  useCreateCompositeUnitMutation,
  useCreateTertiaryUnitMutation,
  useUpdateUnitMutation,
  useDeleteUnitMutation,
  useCreateProductWithInventoryMutation,
} = baseQuery;
